// Reservations screen.
//
// Lives entirely against the backend — no more mockData.  Loads every
// reservation, supports filtering by status + free-text search, lets staff
// create bookings (with an existing guest or a brand-new one), edit a
// reservation that hasn't been checked out, and cancel before arrival.
//
// Check-in / check-out actions live primarily on the CheckInOut screen,
// but we expose a "Check In" shortcut here for confirmed bookings so the
// front desk doesn't have to flip tabs.

import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import {
  reservationsUrl,
  reservationByIdUrl,
  reservationCheckInUrl,
  reservationCancelUrl,
  guestsUrl,
  roomsUrl,
  roomTypesUrl,
} from "../../BaseURL";
import {
  computeBillingClient,
  defaultStartForMode,
  endForMode,
  toDateTimeLocalValue,
} from "../../utils/billing";
import StayWindowPicker from "../../components/StayWindowPicker";
import SelectedRoomCard from "../../components/SelectedRoomCard";
import MultiRoomEditor, { BLANK_ROOM_LINE } from "../../components/MultiRoomEditor";
import DepositHint, { useDepositGuard } from "../../components/DepositHint";
import { useNowTick } from "../../hooks/useNowTick";

const STATUS_FILTERS = [
  { id: "all",         label: "All" },
  { id: "pending",     label: "Pending" },
  { id: "confirmed",   label: "Confirmed" },
  { id: "checked_in",  label: "Checked In" },
  { id: "checked_out", label: "Checked Out" },
  { id: "cancelled",   label: "Cancelled" },
];

const PAYMENT_METHODS = ["Cash", "Card", "Mobile Money", "Transfer"];

// Matches the server's CHECK_IN_CANCEL_WINDOW_MS — regular staff can
// cancel a check-in for this many ms after `actual_check_in_at`.  After
// that, only a super_admin can.
const CANCEL_WINDOW_MS = 30 * 60 * 1000;
const BILLING_MODE_OPTIONS = [
  { id: "nightly", label: "Nightly",  hint: "Per midnight crossed" },
  { id: "hourly",  label: "Hourly",   hint: "Per hour" },
  { id: "daily",   label: "Daily",    hint: "Per 24-hour block" },
];

const BLANK_FORM = {
  guest_id: "",
  guest:    { full_name: "", phone: "", email: "", id_type: "", id_number: "" },
  // `rooms` is the source of truth — a list of room lines.  Single-room
  // bookings keep length 1.  Multi-room bookings grow / shrink it via
  // the MultiRoomEditor.  All rooms share the same booking-level dates
  // unless an entry overrides them.
  rooms: [{ ...BLANK_ROOM_LINE }],
  billing_mode:  "nightly",
  check_in_at:   "",
  check_out_at:  "",
  paid_amount: "",
  payment_method: "Cash",
  source: "walk_in",
  notes: "",
};

export default function Reservations() {
  const [reservations, setReservations] = useState([]);
  const [rooms, setRooms]       = useState([]);
  const [roomTypes, setRoomTypes] = useState([]);
  const [guests, setGuests]     = useState([]);
  const [filter, setFilter]     = useState("all");
  const [search, setSearch]     = useState("");
  const [loading, setLoading]   = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm]         = useState(BLANK_FORM);
  const [guestMode, setGuestMode] = useState("new"); // "new" | "existing"
  const [submitting, setSubmitting] = useState(false);
  const [msg, setMsg]           = useState({ type: "", text: "" });
  const [confirmCancel, setConfirmCancel] = useState(null);

  // Role check for the cancel-after-30-min override.
  const me = (() => {
    try { return JSON.parse(localStorage.getItem("user") || "null"); }
    catch (_) { return null; }
  })();
  const isSuperAdmin = me?.role === "super_admin";

  // 1-second clock that powers every live countdown in this table.
  const now = useNowTick(1000);

  // Renders a millisecond delta as HH:MM:SS (minus sign when overdue).
  const fmtCountdown = (ms) => {
    const sign = ms < 0 ? "−" : "";
    const abs = Math.abs(Math.floor(ms / 1000));
    const h = Math.floor(abs / 3600);
    const m = Math.floor((abs % 3600) / 60);
    const s = abs % 60;
    const p = (n) => String(n).padStart(2, "0");
    return `${sign}${p(h)}:${p(m)}:${p(s)}`;
  };

  const showMsg = (type, text) => {
    setMsg({ type, text });
    setTimeout(() => setMsg({ type: "", text: "" }), 4500);
  };

  // ── Loaders ───────────────────────────────────────────────────────────────
  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [rRes, roomRes, guestRes, typeRes] = await Promise.all([
        axios.get(reservationsUrl, { params: { status: filter !== "all" ? filter : undefined } }),
        axios.get(roomsUrl),
        axios.get(guestsUrl),
        axios.get(roomTypesUrl),
      ]);
      setReservations(rRes.data?.results || []);
      setRooms(roomRes.data?.results || []);
      setGuests(guestRes.data?.results || []);
      setRoomTypes(typeRes.data?.results || []);
    } catch (err) {
      console.error(err);
      showMsg("error", err.response?.data?.message || "Could not load reservations");
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { loadAll(); }, [loadAll]);

  // Rooms that aren't dead-locked.  We INCLUDE "reserved" — staff often
  // assign the same room across non-overlapping windows; the backend's
  // overlap check still protects them.  Editing keeps the current room
  // selectable even if it's currently occupied.
  const selectableRooms = useMemo(() => {
    return rooms.filter(r =>
      r.is_active === 1 &&
      !['maintenance','out_of_order'].includes(r.status)
    );
  }, [rooms]);

  const filteredList = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return reservations;
    return reservations.filter(r =>
      (r.code || "").toLowerCase().includes(term) ||
      (r.guest_name || "").toLowerCase().includes(term) ||
      String(r.room_number || "").toLowerCase().includes(term)
    );
  }, [reservations, search]);

  // ── Helpers ───────────────────────────────────────────────────────────────
  const fmtDateTime = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso);
    return d.toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
  };

  // Rate cascade for a chosen room: room.price_per_night first, otherwise
  // the room type's defaults.  Used when opening the create form.
  const ratesForRoom = (roomId) => {
    const room = rooms.find(r => String(r.id) === String(roomId));
    if (!room) return { nightly: "", hourly: "", daily: "" };
    const type = roomTypes.find(t => t.id === room.room_type_id);
    return {
      nightly: room.price_per_night || type?.base_price || "",
      hourly:  type?.hourly_rate    || "",
      daily:   type?.daily_rate     || "",
    };
  };
  const money = (n) => `GH₵ ${Number(n || 0).toFixed(2)}`;
  const statusBadge = (s) => {
    const cls =
      s === "checked_in"  ? "badge-green"  :
      s === "checked_out" ? "badge-gray"   :
      s === "confirmed"   ? "badge-gold"   :
      s === "pending"     ? "badge-blue"   :
                            "badge-red";
    return <span className={`badge ${cls}`}>{s.replace("_", " ")}</span>;
  };

  // ── Form handlers ─────────────────────────────────────────────────────────
  const openCreate = () => {
    // Seed the stay window from the form's current billing mode so the
    // StayWindowPicker opens already coherent (nightly → 6 PM today, etc.).
    const mode  = BLANK_FORM.billing_mode || "nightly";
    const start = defaultStartForMode(mode);
    setForm({
      ...BLANK_FORM,
      rooms: [{ ...BLANK_ROOM_LINE }],
      check_in_at:  start,
      check_out_at: endForMode(mode, start),
    });
    setGuestMode("new");
    setEditingId(null);
    setShowForm(true);
  };

  // Edit always operates on a single reservation row — multi-room
  // bookings are edited per-room (you can change one of the rooms in a
  // group without disturbing the others).
  const openEdit = (r) => {
    setForm({
      guest_id: r.guest_id,
      guest:    { full_name: r.guest_name, phone: r.guest_phone || "", email: r.guest_email || "" },
      rooms: [{
        room_id: r.room_id || "",
        adults:   r.adults,
        children: r.children,
        rate_per_night: r.rate_per_night,
        rate_per_hour:  r.rate_per_hour,
        rate_per_day:   r.rate_per_day,
      }],
      billing_mode:  r.billing_mode || "nightly",
      check_in_at:   toDateTimeLocalValue(r.check_in_at  || r.check_in_date),
      check_out_at:  toDateTimeLocalValue(r.check_out_at || r.check_out_date),
      paid_amount:    r.paid_amount,
      payment_method: r.payment_method || "Cash",
      source: r.source || "walk_in",
      notes:  r.notes || "",
    });
    setGuestMode("existing");
    setEditingId(r.id);
    setShowForm(true);
  };

  // Live total preview — sums per-room totals so multi-room bookings
  // show a sensible grand total under the form.
  const formBilling = useMemo(() => {
    const perRoom = (form.rooms || []).map(line => computeBillingClient({
      billing_mode:   form.billing_mode,
      check_in_at:    form.check_in_at,
      check_out_at:   form.check_out_at,
      rate_per_night: line.rate_per_night,
      rate_per_hour:  line.rate_per_hour,
      rate_per_day:   line.rate_per_day,
    }));
    const okAll = perRoom.every(b => b.ok);
    const total = perRoom.reduce((s, b) => s + (Number(b.total) || 0), 0);
    const first = perRoom[0] || { mode: form.billing_mode, nights: 0, hours: 0 };
    return {
      ok: okAll, total,
      mode: first.mode,
      perRoom,
      label: okAll
        ? (perRoom.length > 1
            ? `${perRoom.length} rooms · GH₵ ${total.toFixed(2)}`
            : (first.label || `GH₵ ${total.toFixed(2)}`))
        : "Enter check-in / check-out to see total",
    };
  }, [form.billing_mode, form.check_in_at, form.check_out_at, form.rooms]);

  // Deposit-policy guard.  When the policy is on (Settings → Deposit
  // Policy), the operator can't save / check in until at least the
  // required amount is collected.  Server enforces the same rule.
  const depositGuard = useDepositGuard({
    total: formBilling.total,
    paid:  form.paid_amount,
  });
  const depositOk = !depositGuard.enabled
    || (Number(form.paid_amount) || 0) >= Math.min(
         Number(depositGuard.required) || 0,
         Number(formBilling.total)     || 0,
       );

  const submit = async (e, walkInNow = false) => {
    e?.preventDefault?.();
    setSubmitting(true);
    try {
      // Build the rooms[] payload from the form's lines.  We always send
      // a `rooms` array — the backend handles single-room and multi-room
      // through the same code path.
      const roomsPayload = (form.rooms || [])
        .filter(line => line.room_id)
        .map(line => ({
          room_id: Number(line.room_id),
          adults:  Number(line.adults)   || 1,
          children: Number(line.children) || 0,
          rate_per_night: line.rate_per_night === "" || line.rate_per_night == null ? undefined : Number(line.rate_per_night),
          rate_per_hour:  line.rate_per_hour  === "" || line.rate_per_hour  == null ? undefined : Number(line.rate_per_hour),
          rate_per_day:   line.rate_per_day   === "" || line.rate_per_day   == null ? undefined : Number(line.rate_per_day),
        }));

      if (roomsPayload.length === 0) {
        showMsg("error", "Pick at least one room.");
        return;
      }

      const payload = {
        check_in_at:  form.check_in_at,
        check_out_at: form.check_out_at,
        billing_mode: form.billing_mode,
        paid_amount:    form.paid_amount    === "" ? 0 : Number(form.paid_amount),
        payment_method: form.payment_method,
        source: form.source,
        notes: form.notes || undefined,
        walk_in_now: walkInNow,
      };

      // Edit hits a single reservation — keep the legacy single-room
      // payload shape since the PUT route still works one row at a time.
      if (editingId) {
        const line = roomsPayload[0];
        payload.room_id = line.room_id;
        payload.adults  = line.adults;
        payload.children = line.children;
        payload.rate_per_night = line.rate_per_night;
        payload.rate_per_hour  = line.rate_per_hour;
        payload.rate_per_day   = line.rate_per_day;
      } else {
        payload.rooms = roomsPayload;
      }

      if (guestMode === "existing" && form.guest_id) {
        payload.guest_id = Number(form.guest_id);
      } else {
        payload.guest = form.guest;
      }

      if (editingId) {
        const res = await axios.put(reservationByIdUrl(editingId), payload);
        if (!res.data?.success) { showMsg("error", res.data?.message || "Update failed"); return; }
        showMsg("success", "Reservation updated.");
      } else {
        const res = await axios.post(reservationsUrl, payload);
        if (!res.data?.success) { showMsg("error", res.data?.message || "Could not create"); return; }
        const grp = res.data.booking_group_code;
        const n   = res.data.reservations?.length || 1;
        if (n > 1) {
          showMsg("success", walkInNow
            ? `Checked in ${n} rooms · group ${grp}`
            : `${n} rooms reserved · group ${grp}`);
        } else {
          showMsg("success", walkInNow
            ? `Checked in — ${res.data.code}`
            : `Reservation ${res.data.code} created`);
        }
      }
      setShowForm(false);
      setForm(BLANK_FORM);
      setEditingId(null);
      await loadAll();
    } catch (err) {
      showMsg("error", err.response?.data?.message || "Operation failed");
    } finally {
      setSubmitting(false);
    }
  };

  const quickCheckIn = async (r) => {
    try {
      const res = await axios.patch(reservationCheckInUrl(r.id));
      if (!res.data?.success) { showMsg("error", res.data?.message || "Check-in failed"); return; }
      showMsg("success", `${r.guest_name} checked in.`);
      await loadAll();
    } catch (err) {
      showMsg("error", err.response?.data?.message || "Check-in failed");
    }
  };

  const cancelReservation = async () => {
    if (!confirmCancel) return;
    try {
      const res = await axios.patch(reservationCancelUrl(confirmCancel.id), {
        reason: confirmCancel.reason || null,
      });
      if (!res.data?.success) { showMsg("error", res.data?.message || "Cancel failed"); return; }
      showMsg("success", `${confirmCancel.code} cancelled.`);
      setConfirmCancel(null);
      await loadAll();
    } catch (err) {
      showMsg("error", err.response?.data?.message || "Cancel failed");
    }
  };

  // ── Summary stats ─────────────────────────────────────────────────────────
  const counts = useMemo(() => {
    const c = { total: reservations.length, pending: 0, confirmed: 0, checked_in: 0, checked_out: 0, cancelled: 0 };
    for (const r of reservations) if (c[r.status] !== undefined) c[r.status]++;
    return c;
  }, [reservations]);

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: 22 }}>
        <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 30, color: "#2c1f14", fontWeight: 500, marginBottom: 4 }}>
          Reservations
        </div>
        <div style={{ fontSize: 14.5, color: "#6b5142" }}>
          Book new stays, manage arrivals, and track every reservation through to checkout.
        </div>
      </div>

      {/* Stats */}
      <div className="g4" style={{ marginBottom: 22 }}>
        {[
          { lbl: "Total",        val: counts.total,        sub: "All reservations" },
          { lbl: "Confirmed",    val: counts.confirmed,    sub: "Awaiting arrival" },
          { lbl: "In House",     val: counts.checked_in,   sub: "Currently staying" },
          { lbl: "Checked Out",  val: counts.checked_out,  sub: "Completed stays" },
        ].map((s, i) => (
          <div className="stat" key={i} style={{ animationDelay: `${i * 0.05}s` }}>
            <div className="stat-val">{loading ? "…" : s.val}</div>
            <div className="stat-lbl">{s.lbl}</div>
            <div className="stat-sub">{s.sub}</div>
          </div>
        ))}
      </div>

      {/* Filters + action */}
      <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap", alignItems: "center" }}>
        {STATUS_FILTERS.map(f => (
          <button
            key={f.id}
            className={`btn-outline ${filter === f.id ? "active" : ""}`}
            onClick={() => setFilter(f.id)}
          >
            {f.label}
          </button>
        ))}
        <input
          className="f-input"
          style={{ width: 240, padding: "9px 12px", marginLeft: 8 }}
          placeholder="🔍  Search code / guest / room…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <button className="btn-dark" style={{ marginLeft: "auto" }} onClick={openCreate}>
          + New Reservation
        </button>
      </div>

      {msg.text && (
        <div style={{
          padding: "12px 16px", borderRadius: 8, marginBottom: 16,
          background: msg.type === "success" ? "#eaf5ee" : "#fef0ee",
          color:      msg.type === "success" ? "#2d6a4f" : "#a93226",
          border:     `1px solid ${msg.type === "success" ? "#a8d5b5" : "#f5c5bc"}`,
          fontSize: 14, fontWeight: 400,
        }}>
          {msg.type === "success" ? "✅" : "⚠️"} {msg.text}
        </div>
      )}

      {/* Table */}
      <div className="panel">
        <div className="panel-hd">
          <span className="panel-hd-title">Reservations</span>
          <span className="badge badge-gold">{filteredList.length} records</span>
        </div>
        <div style={{ overflowX: "auto" }}>
          <table className="tbl">
            <thead>
              <tr>
                <th>Code</th><th>Guest</th><th>Room</th>
                <th>Check In</th><th>Check Out</th>
                <th style={{ textAlign: "center" }}>Nights</th>
                <th>Total</th><th>Paid</th><th>Balance</th>
                <th>Status</th><th style={{ textAlign: "right" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={11} style={{ textAlign: "center", padding: 40, color: "#6b5142" }}>Loading…</td></tr>
              ) : filteredList.length === 0 ? (
                <tr><td colSpan={11} style={{ textAlign: "center", padding: 40, color: "#6b5142" }}>
                  No reservations {filter !== "all" ? `with status "${filter.replace("_", " ")}"` : ""}.
                </td></tr>
              ) : filteredList.map(r => {
                // ── Live countdowns for checked-in rows ──────────────
                // Same math as the CheckInOut in-house panel: time until
                // scheduled checkout (red when overdue, amber under 30
                // min) and time left in the 30-min cancel window.  Only
                // computed for checked_in rows — everywhere else these
                // values are hidden.
                const isIn = r.status === "checked_in";
                const outMs = isIn ? new Date(r.check_out_at || r.check_out_date).getTime() : 0;
                const toOutMs = isIn ? outMs - now : 0;
                const overdue = isIn && toOutMs <= 0;
                const minsToOut = toOutMs / 60000;
                const outColor =
                  !isIn                   ? null      :
                  overdue                 ? "#a93226" :
                  minsToOut <= 5          ? "#a93226" :
                  minsToOut <= 30         ? "#a85618" :
                                            "#2d6a4f";

                const checkedInMs = r.actual_check_in_at
                  ? new Date(r.actual_check_in_at).getTime() : null;
                const cancelLeftMs = checkedInMs
                  ? (checkedInMs + CANCEL_WINDOW_MS) - now : 0;
                const inWindow = cancelLeftMs > 0;
                const canCancel = isIn ? (inWindow || isSuperAdmin) : false;

                return (
                <tr key={r.id}>
                  <td style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 16, color: "#2c1f14" }}>
                    {r.code}
                    {r.booking_group_code && (
                      <div
                        title={`Part of multi-room booking ${r.booking_group_code}`}
                        style={{
                          marginTop: 2,
                          display: "inline-block",
                          padding: "1px 7px",
                          borderRadius: 999,
                          background: "rgba(58,123,213,.10)",
                          color: "#1a5a8a",
                          fontFamily: "'Jost', sans-serif",
                          fontSize: 9.5,
                          letterSpacing: ".1em",
                          textTransform: "uppercase",
                          fontWeight: 600,
                        }}>
                        🧩 {r.booking_group_code}
                      </div>
                    )}
                  </td>
                  <td>
                    <div style={{ fontWeight: 500, color: "#2c1f14" }}>{r.guest_name}</div>
                    {r.guest_phone && <div style={{ fontSize: 12, color: "#6b5142" }}>{r.guest_phone}</div>}
                  </td>
                  <td>
                    {r.room_number || "—"}
                    {r.room_type_name && <span style={{ fontSize: 11, color: "#8a7060", marginLeft: 4 }}>({r.room_type_name})</span>}
                  </td>
                  <td title={fmtDateTime(r.check_in_at || r.check_in_date)}>{fmtDateTime(r.check_in_at || r.check_in_date)}</td>
                  <td title={fmtDateTime(r.check_out_at || r.check_out_date)}>
                    {fmtDateTime(r.check_out_at || r.check_out_date)}
                    {isIn && (
                      <div style={{
                        marginTop: 4,
                        fontSize: 11.5,
                        fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                        color: outColor, fontWeight: 500,
                      }}>
                        {overdue ? "⏰ " : "⏱ "}{fmtCountdown(toOutMs)}
                      </div>
                    )}
                  </td>
                  <td style={{ textAlign: "center" }}>
                    {r.billing_mode === "hourly"
                      ? <span title="hourly">{Number(r.hours).toFixed(1)}h</span>
                      : <span title={r.billing_mode || "nightly"}>{r.nights}{r.billing_mode === "daily" ? "d" : "n"}</span>}
                  </td>
                  <td>{money(r.total_amount)}</td>
                  <td>{money(r.paid_amount)}</td>
                  <td>
                    <span style={{ color: Number(r.balance) > 0 ? "#a93226" : "#2d6a4f", fontWeight: 500 }}>
                      {money(r.balance)}
                    </span>
                  </td>
                  <td>{statusBadge(r.status)}</td>
                  <td style={{ textAlign: "right" }}>
                    <div style={{ display: "inline-flex", gap: 6 }}>
                      {['pending','confirmed'].includes(r.status) && (
                        <>
                          <button className="btn-gold" style={{ padding: "6px 12px", fontSize: 11 }} onClick={() => quickCheckIn(r)}>
                            Check In
                          </button>
                          <button className="btn-outline" style={{ padding: "6px 12px", fontSize: 11 }} onClick={() => openEdit(r)}>
                            Edit
                          </button>
                          <button
                            onClick={() => setConfirmCancel({ id: r.id, code: r.code, reason: "" })}
                            style={{ padding: "6px 12px", fontSize: 11, background: "transparent", border: "1.5px solid rgba(192,57,43,.3)", borderRadius: 7, color: "#a93226", cursor: "pointer", fontFamily: "'Jost',sans-serif", letterSpacing: ".08em", textTransform: "uppercase", fontWeight: 500 }}
                          >
                            Cancel
                          </button>
                        </>
                      )}
                      {isIn && (
                        <>
                          <button className="btn-outline" style={{ padding: "6px 12px", fontSize: 11 }} onClick={() => openEdit(r)}>
                            Edit
                          </button>
                          <button
                            onClick={() => canCancel && setConfirmCancel({ id: r.id, code: r.code, reason: "" })}
                            disabled={!canCancel}
                            title={canCancel
                              ? (inWindow
                                  ? `Cancel window: ${fmtCountdown(cancelLeftMs)} left`
                                  : "Super admin cancel")
                              : "Cancel window expired — super admin only"}
                            style={{
                              padding: "6px 12px", fontSize: 11,
                              background: "transparent",
                              border: `1.5px solid ${canCancel ? "rgba(192,57,43,.3)" : "rgba(180,140,80,.2)"}`,
                              borderRadius: 7,
                              color: canCancel ? "#a93226" : "#b8a090",
                              cursor: canCancel ? "pointer" : "not-allowed",
                              fontFamily: "'Jost',sans-serif",
                              letterSpacing: ".08em", textTransform: "uppercase",
                              fontWeight: 500,
                              opacity: canCancel ? 1 : 0.7,
                            }}
                          >
                            Cancel
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Form modal ───────────────────────────────────────────────────── */}
      {showForm && (
        <div className="modal-overlay" onClick={() => !submitting && setShowForm(false)}>
          <div
            className="modal-box"
            style={{ width: "min(760px, 96vw)", maxHeight: "92vh", overflow: "auto" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <span className="modal-title">{editingId ? "Edit Reservation" : "New Reservation"}</span>
              <button className="modal-close" onClick={() => !submitting && setShowForm(false)}>✕</button>
            </div>
            <form className="modal-body" onSubmit={(e) => submit(e, false)}>
              {/* Guest selector */}
              {!editingId && (
                <div className="f-group">
                  <label className="f-label">Guest</label>
                  <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
                    <button type="button" className={`btn-outline ${guestMode === "new" ? "active" : ""}`} onClick={() => setGuestMode("new")}>
                      + New Guest
                    </button>
                    <button type="button" className={`btn-outline ${guestMode === "existing" ? "active" : ""}`} onClick={() => setGuestMode("existing")}>
                      Use Existing
                    </button>
                  </div>
                  {guestMode === "existing" ? (
                    <select
                      className="f-select"
                      value={form.guest_id}
                      onChange={(e) => setForm({ ...form, guest_id: e.target.value })}
                      required
                    >
                      <option value="">Select a guest…</option>
                      {guests.map(g => (
                        <option key={g.id} value={g.id}>
                          {g.full_name}{g.phone ? ` — ${g.phone}` : ""}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <div className="form-grid">
                      <input className="f-input" placeholder="Full name *" value={form.guest.full_name}
                        onChange={(e) => setForm({ ...form, guest: { ...form.guest, full_name: e.target.value } })} required/>
                      <input className="f-input" type="tel" required
                        placeholder="Phone * (for SMS welcome / receipts)"
                        value={form.guest.phone}
                        onChange={(e) => setForm({ ...form, guest: { ...form.guest, phone: e.target.value } })}/>
                      <input className="f-input" type="email" placeholder="Email" value={form.guest.email}
                        onChange={(e) => setForm({ ...form, guest: { ...form.guest, email: e.target.value } })}/>
                      <input className="f-input" placeholder="ID / Passport number" value={form.guest.id_number}
                        onChange={(e) => setForm({ ...form, guest: { ...form.guest, id_number: e.target.value } })}/>
                    </div>
                  )}
                </div>
              )}

              {/* Source + Billing mode + Dates (shared across every room
                  in the booking) */}
              <div className="form-grid">
                <div className="f-group">
                  <label className="f-label">Source</label>
                  <select className="f-select" value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })}>
                    <option value="walk_in">Walk-in</option>
                    <option value="phone">Phone</option>
                    <option value="online">Online</option>
                    <option value="agent">Agent</option>
                  </select>
                </div>
                <div className="f-group">
                  <label className="f-label">Payment method</label>
                  <select className="f-select" value={form.payment_method} onChange={(e) => setForm({ ...form, payment_method: e.target.value })}>
                    {PAYMENT_METHODS.map(m => <option key={m} value={m}>{m}</option>)}
                  </select>
                </div>
                <div className="f-group" style={{ gridColumn: "1 / -1" }}>
                  <label className="f-label">Billing Mode</label>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    {BILLING_MODE_OPTIONS.map(m => {
                      const on = form.billing_mode === m.id;
                      return (
                        <button key={m.id} type="button"
                          onClick={() => setForm({ ...form, billing_mode: m.id })}
                          style={{
                            flex: 1, minWidth: 90,
                            padding: "9px 12px", borderRadius: 8, cursor: "pointer",
                            fontFamily: "'Jost', sans-serif",
                            background: on ? "linear-gradient(135deg, #c8a05a, #a8814a)" : "transparent",
                            color: on ? "#1a110a" : "#4a3828",
                            border: `1.5px solid ${on ? "#a8814a" : "rgba(180,140,80,.3)"}`,
                            fontWeight: on ? 600 : 500,
                            letterSpacing: ".06em",
                          }}>
                          <div style={{ fontSize: 12, textTransform: "uppercase" }}>{m.label}</div>
                          <div style={{ fontSize: 10.5, fontWeight: 400, opacity: .8, marginTop: 2 }}>{m.hint}</div>
                        </button>
                      );
                    })}
                  </div>
                </div>
                {/* Guided check-in / check-out window.  Component locks
                    the inputs to match `billing_mode` so the operator
                    can't accidentally set a 3 AM nightly arrival or a
                    daily stay that's not exactly 24 h. */}
                <StayWindowPicker
                  mode={form.billing_mode}
                  checkIn={form.check_in_at}
                  checkOut={form.check_out_at}
                  onChange={({ check_in_at, check_out_at }) =>
                    setForm((f) => ({ ...f, check_in_at, check_out_at }))
                  }
                />
                <div className="f-group">
                  <label className="f-label">Amount paid now (GH₵)</label>
                  <input className="f-input" type="number" step="0.01" placeholder="0"
                    value={form.paid_amount}
                    onChange={(e) => setForm({ ...form, paid_amount: e.target.value })}/>
                </div>
              </div>

              {/* Rooms — one or many.  Hidden when editing an existing
                  reservation (edits are per-room; multi-room editing
                  would need new server endpoints). */}
              {editingId ? (
                <div className="f-group">
                  <label className="f-label">Room *</label>
                  <select
                    className="f-select"
                    value={form.rooms[0]?.room_id || ""}
                    onChange={(e) => {
                      const id = e.target.value;
                      const { nightly, hourly, daily } = ratesForRoom(id);
                      const next = { ...form.rooms[0],
                        room_id: id,
                        rate_per_night: form.rooms[0]?.rate_per_night || nightly || "",
                        rate_per_hour:  form.rooms[0]?.rate_per_hour  || hourly  || "",
                        rate_per_day:   form.rooms[0]?.rate_per_day   || daily   || "",
                      };
                      setForm({ ...form, rooms: [next] });
                    }}
                    required
                  >
                    <option value="">Select room…</option>
                    {selectableRooms.map(r => (
                      <option key={r.id} value={r.id}>
                        {r.room_number} — {r.room_type_name || "Untyped"} ({money(r.price_per_night)})
                      </option>
                    ))}
                  </select>
                  <SelectedRoomCard
                    room={rooms.find(r => String(r.id) === String(form.rooms[0]?.room_id))}
                    money={money}
                  />
                </div>
              ) : (
                <MultiRoomEditor
                  rooms={selectableRooms}
                  roomTypes={roomTypes}
                  value={form.rooms}
                  billingMode={form.billing_mode}
                  onChange={(next) => setForm({ ...form, rooms: next })}
                  money={money}
                />
              )}

              {/* Live total preview */}
              <div style={{
                marginBottom: 14, padding: "12px 14px", borderRadius: 9,
                background: formBilling.ok
                  ? "linear-gradient(135deg, rgba(200,160,90,.10), rgba(200,160,90,.04))"
                  : "rgba(192,57,43,.06)",
                border: `1px solid ${formBilling.ok ? "rgba(200,160,90,.3)" : "rgba(192,57,43,.25)"}`,
                display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10,
              }}>
                <div>
                  <div style={{ fontSize: 10.5, letterSpacing: ".14em", textTransform: "uppercase", color: "#6b5142", fontWeight: 500 }}>
                    Stay Total
                  </div>
                  <div style={{ fontSize: 12.5, color: "#4a3828", marginTop: 2 }}>
                    {formBilling.label || "Enter check-in / check-out to see total"}
                  </div>
                </div>
                <div style={{
                  fontFamily: "'Cormorant Garamond', serif",
                  fontSize: 22, color: "#2c1f14", fontWeight: 600,
                }}>
                  {money(formBilling.total)}
                </div>
              </div>

              {/* Deposit policy banner — only shows when the rule is on. */}
              <DepositHint total={formBilling.total} paid={form.paid_amount} />

              <div className="f-group" style={{ marginTop: 14 }}>
                <label className="f-label">Notes</label>
                <textarea className="f-input" rows={2} placeholder="Any special requests / context…"
                  value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}/>
              </div>

              <div style={{ display: "flex", gap: 10, marginTop: 8 }}>
                {!editingId && (
                  <button type="button" className="btn-gold" style={{ flex: 1 }}
                    disabled={submitting || !depositOk}
                    title={depositOk ? undefined : "Collect the required deposit first"}
                    onClick={(e) => submit(e, true)}>
                    {submitting ? "Saving…" : !depositOk ? "🔒 Deposit required" : "Check In Now"}
                  </button>
                )}
                <button type="submit" className="btn-dark" style={{ flex: 1 }}
                  disabled={submitting || !depositOk}
                  title={depositOk ? undefined : "Collect the required deposit first"}>
                  {submitting ? "Saving…"
                   : !depositOk ? "🔒 Deposit required"
                   : editingId  ? "Save Changes"
                                : "Save Reservation"}
                </button>
                <button type="button" className="btn-outline" onClick={() => setShowForm(false)} disabled={submitting}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Cancel confirmation ──────────────────────────────────────────── */}
      {confirmCancel && (
        <div className="modal-overlay" onClick={() => setConfirmCancel(null)}>
          <div className="modal-box" style={{ width: "min(460px, 92vw)" }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <span className="modal-title">Cancel {confirmCancel.code}?</span>
              <button className="modal-close" onClick={() => setConfirmCancel(null)}>✕</button>
            </div>
            <div className="modal-body">
              <p style={{ fontSize: 14.5, color: "#3a2a1a", marginBottom: 16, lineHeight: 1.55 }}>
                The reservation will be marked cancelled and the room will be freed for other guests.
              </p>
              <div className="f-group">
                <label className="f-label">Reason (optional)</label>
                <input className="f-input" placeholder="e.g. Guest cancelled by phone"
                  value={confirmCancel.reason}
                  onChange={(e) => setConfirmCancel({ ...confirmCancel, reason: e.target.value })}/>
              </div>
              <div style={{ display: "flex", gap: 10 }}>
                <button
                  onClick={cancelReservation}
                  style={{ flex: 1, padding: 12, background: "#a93226", color: "#fff", border: "none", borderRadius: 7, fontFamily: "'Jost',sans-serif", fontSize: 12.5, fontWeight: 500, letterSpacing: ".14em", textTransform: "uppercase", cursor: "pointer" }}
                >
                  Yes, Cancel
                </button>
                <button className="btn-outline" style={{ flex: 1 }} onClick={() => setConfirmCancel(null)}>
                  Keep It
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
